import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  Flex,
  Text,
  Button,
  Select,
  Separator,
  Box,
} from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useGetAllBrands, useSaveSevenRoomsMappings } from "hooks";
import { SevenRoomsDataFieldsSection, SevenRoomsLocationPair, SevenRoomsLocationPairHeaders, SevenRoomsReadonlyField } from "./SevenRoomsDataFieldsSection";
import { SevenRoomsSyncDirectionsSection } from "./SevenRoomsPullSection";
import { SevenRoomsPullScopeDialog } from "./SevenRoomsPullScopeDialog";
import { SevenRoomsPushScopeDialog } from "./SevenRoomsPushScopeDialog";
import {
  SevenRoomsClientTagSection,
  SevenRoomsRequiredFieldsNotice,
} from "./SevenRoomsClientTagSection";
import { normalizeSevenRoomsFieldMappings } from "./sevenroomsFieldMappings";
import "./sevenroomsLayout.css";

export function SevenRoomsMappingDialog({
  isOpen,
  onOpenChange,
  venues = [],
  initialMappings = [],
  initialFieldMappings = null,
  initialPushEnabled = true,
  initialPullEnabled = false,
  initialLastPullTime = null,
  initialClientTagGroup = "",
  initialClientTagName = "",
  reconnectAction = null,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToastMessage } = useToast();
  const { userProfile } = useProfileContext();
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  const { locationsData } = useLocationContext();
  const saveSevenRoomsMappings = useSaveSevenRoomsMappings();

  const [sevenroomsVenues, setSevenRoomsVenues] = useState([]);
  const [bconnectBrands, setBconnectBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);
  const [locationMappings, setLocationMappings] = useState({});
  const [brandListSelections, setBrandListSelections] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasRestoredMappings, setHasRestoredMappings] = useState(false);
  const [fieldMappings, setFieldMappings] = useState(() =>
    normalizeSevenRoomsFieldMappings(initialFieldMappings)
  );
  const [pushEnabled, setPushEnabled] = useState(Boolean(initialPushEnabled));
  const [pullEnabled, setPullEnabled] = useState(Boolean(initialPullEnabled));
  const [pushScope, setPushScope] = useState(null);
  const [pushScopeDialogOpen, setPushScopeDialogOpen] = useState(false);
  const [pullScope, setPullScope] = useState(null);
  const [pullScopeDialogOpen, setPullScopeDialogOpen] = useState(false);
  const [clientTagGroup, setClientTagGroup] = useState(initialClientTagGroup || "");
  const [clientTagName, setClientTagName] = useState(initialClientTagName || "");
  const needsPushScope = !initialPushEnabled;
  const needsPullScope = !initialPullEnabled;

  useEffect(() => {
    if (!isOpen) return;

    setSevenRoomsVenues(
      (Array.isArray(venues) ? venues : []).map((l) => ({
        id: String(l.id ?? ""),
        name: l.name,
      }))
    );
    setBrandListSelections({});

    const next = {};
    (Array.isArray(initialMappings) ? initialMappings : []).forEach((m) => {
      if (m.bconnect_location_id == null || !m.sevenrooms_venue_id) return;
      const key = String(m.bconnect_location_id);
      next[key] = {
        sevenrooms_venue_id: String(m.sevenrooms_venue_id),
        isMapped: true,
      };
    });

    setLocationMappings(next);
    setHasRestoredMappings(
      Object.keys(next).length > 0 &&
        (reconnectAction === "resume" || reconnectAction === "continue_from_now")
    );
    setFieldMappings(normalizeSevenRoomsFieldMappings(initialFieldMappings));
    setPushEnabled(Boolean(initialPushEnabled));
    setPullEnabled(Boolean(initialPullEnabled));
    setPushScope(null);
    setPushScopeDialogOpen(false);
    setPullScope(null);
    setPullScopeDialogOpen(false);
    setClientTagGroup(initialClientTagGroup || "");
    setClientTagName(initialClientTagName || "");
  }, [isOpen, venues, initialMappings, reconnectAction, initialFieldMappings, initialPushEnabled, initialPullEnabled, initialLastPullTime, initialClientTagGroup, initialClientTagName]);

  useEffect(() => {
    if (!brandsData || !locationsData) return;
    const defaultBrand = brandsData.find((b) => b.is_default);
    setBconnectBrands(brandsData.filter((b) => !b.is_default) || []);
    setIndependentLocations(
      locationsData.filter(
        (loc) => loc.brand_id === defaultBrand?.id || loc.brand_id === null
      ) || []
    );
  }, [brandsData, locationsData]);

  const brandLocationsMap = useMemo(() => {
    const map = {};
    (bconnectBrands || []).forEach((b) => {
      const sourceBrand = (brandsData || []).find((bd) => String(bd.id) === String(b.id));
      if (sourceBrand && Array.isArray(sourceBrand.locations)) {
        map[b.id] = sourceBrand.locations;
      } else {
        map[b.id] = (locationsData || []).filter(
          (loc) => String(loc.brand_id) === String(b.id)
        );
      }
    });
    return map;
  }, [bconnectBrands, brandsData, locationsData]);

  const handleLocationListChange = (bconnectLocationId, venueId) => {
    const key = String(bconnectLocationId);
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[key] || { sevenrooms_venue_id: "", isMapped: false };
      next[key] = { ...existing, sevenrooms_venue_id: venueId, isMapped: Boolean(venueId) };
      return next;
    });
  };

  const handleBrandListSelectionChange = (brandId, listId) => {
    setBrandListSelections((prev) => ({ ...(prev || {}), [String(brandId)]: listId }));
  };

  const handleApplyBrandList = (brandId) => {
    const selectedList = brandListSelections[String(brandId)];
    if (!selectedList) return;
    const brandLocs = brandLocationsMap[brandId] || [];
    setLocationMappings((prev) => {
      const next = { ...(prev || {}) };
      brandLocs.forEach((loc) => {
        const locId = loc.id ?? loc.value ?? loc.location_id;
        const key = String(locId);
        const existing = next[key] || { sevenrooms_venue_id: "", isMapped: false };
        next[key] = {
          ...existing,
          sevenrooms_venue_id: selectedList,
          isMapped: Boolean(selectedList),
        };
      });
      return next;
    });
  };

  const handlePullCheckedChange = (checked) => {
    if (!checked) {
      setPullEnabled(false);
      setPullScope(null);
      return;
    }
    setPullScopeDialogOpen(true);
  };

  const handlePushCheckedChange = (checked) => {
    if (!checked) {
      setPushEnabled(false);
      setPushScope(null);
      return;
    }
    setPushScopeDialogOpen(true);
  };

  const handlePushScopeChosen = (scope) => {
    setPushScope(scope);
    setPushEnabled(true);
    setPushScopeDialogOpen(false);
  };

  const handlePullScopeChosen = (scope) => {
    setPullScope(scope);
    setPullEnabled(true);
    setPullScopeDialogOpen(false);
  };

  const handleSaveLocationMappings = async () => {
    if (!pushEnabled && !pullEnabled) {
      setToastMessage("Enable push, pull, or both for the SevenRooms integration.");
      return;
    }
    if (pushEnabled && needsPushScope && !pushScope) {
      setPushScopeDialogOpen(true);
      setToastMessage("Choose whether pushing resumes from the last sync or starts from now.");
      return;
    }
    if (pullEnabled && needsPullScope && !pullScope) {
      setPullScopeDialogOpen(true);
      setToastMessage("Choose whether to import existing SevenRooms profiles or start from now.");
      return;
    }
    if (Boolean(clientTagGroup.trim()) !== Boolean(clientTagName.trim())) {
      setToastMessage("Enter both the SevenRooms tag group/source name and tag name.");
      return;
    }
    try {
      setIsLoading(true);

      const allLocations = [
        ...(independentLocations || []),
        ...(Object.values(brandLocationsMap || {}).flat() || []),
      ];

      const locationPayload = allLocations
        .map((loc) => {
          const locId = loc.id ?? loc.value ?? loc.location_id;
          const key = String(locId);
          const curr = locationMappings[key] || {};
          const currList = curr.sevenrooms_venue_id || null;
          if (!currList) return null;
          const venueObj = sevenroomsVenues.find((bl) => String(bl.id) === String(currList));
          return {
            bconnect_location_id: locId,
            bconnect_location_name: loc.name ?? loc.label ?? "",
            sevenrooms_venue_id: currList,
            sevenrooms_venue_name: venueObj?.name || "",
          };
        })
        .filter(Boolean);

      const payload = {
        organisation_id: userProfile.organisation_id,
        location_mappings: locationPayload,
        field_mappings: fieldMappings,
        push_enabled: pushEnabled,
        pull_enabled: pullEnabled,
        client_tag_group: clientTagGroup.trim(),
        client_tag_name: clientTagName.trim(),
      };
      if (pushEnabled && pushScope) {
        payload.push_scope = pushScope;
      }
      if (pullEnabled && pullScope) {
        payload.pull_scope = pullScope;
      }

      const result = await saveSevenRoomsMappings.mutateAsync(payload);

      if (result?.data?.status !== "success") {
        throw new Error(result?.data?.message || "Failed to save location mappings");
      }

      queryClient.invalidateQueries(["sevenrooms-status"]);
      queryClient.invalidateQueries(["sevenrooms-mappings", userProfile.organisation_id]);
      queryClient.invalidateQueries(["sevenrooms-venues", userProfile.organisation_id]);
      onOpenChange(false);
      navigate("/activation-success", {
        state: {
          description: "Your customers will now sync with your selected SevenRooms venues.",
          integrationType: "sevenrooms",
        },
      });
    } catch (err) {
      console.error("Error saving SevenRooms location mappings:", err);
      setToastMessage(
        err?.response?.data?.message || err?.message || "Failed to save location mappings"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderLocationRow = (loc) => {
    const locId = loc.id ?? loc.value ?? loc.location_id;
    const key = String(locId);
    const mapping = locationMappings[key] || {};
    const locationName = loc.name ?? loc.label ?? "";
    return (
      <SevenRoomsLocationPair
        key={key}
        left={<SevenRoomsReadonlyField title={locationName}>{locationName}</SevenRoomsReadonlyField>}
        right={
          <Select.Root
            value={mapping.sevenrooms_venue_id || ""}
            onValueChange={(v) => handleLocationListChange(key, v)}
          >
            <Select.Trigger placeholder="Select..." style={{ width: "100%", boxSizing: "border-box" }}>
              <div
                style={{
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  width: "100%",
                }}
                title={
                  mapping.sevenrooms_venue_id
                    ? sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))
                        ?.name || ""
                    : "Select a SevenRooms venue"
                }
              >
                {mapping.sevenrooms_venue_id
                  ? sevenroomsVenues.find((bl) => String(bl.id) === String(mapping.sevenrooms_venue_id))
                      ?.name || ""
                  : "Select a SevenRooms venue"}
              </div>
            </Select.Trigger>
            <Select.Content side="bottom" align="start" sideOffset={4}>
              {sevenroomsVenues.map((bl) => (
                <Select.Item key={bl.id} value={bl.id} title={bl.name}>
                  {bl.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        }
      />
    );
  };

  return (
    <>
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content
        maxWidth="720px"
        width="100%"
        style={{
          height: "min(760px, calc(100vh - 96px))",
          padding: "var(--space-5)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Flex direction="column" gap="4" style={{ flex: "1 1 auto", minHeight: 0 }}>
          <Flex direction="column" gap="2" style={{ flex: "0 0 auto" }}>
            <Dialog.Title>Map locations to SevenRooms venues</Dialog.Title>
            <Dialog.Description size="2" style={{ color: "var(--gray-a11)" }}>
              {hasRestoredMappings
                ? "Your previous location mappings have been restored. Review them below, change anything if needed, then save to finish connecting."
                : "Choose which SevenRooms venue each b connect location should pull guests from and push guests to. You can map each location independently."}
            </Dialog.Description>
          </Flex>

          <Box
            style={{
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              paddingRight: "var(--space-2)",
            }}
          >
            <Flex direction="column" gap="4">
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Brands
                  </Text>
                  <Text size="1" color="gray">
                    Group: b connect brands with each of their locations.
                  </Text>
                </Flex>

                <SevenRoomsLocationPairHeaders />

                {(bconnectBrands || []).length > 0 ? (
                  (bconnectBrands || []).map((b) => {
                    const brandLocations = brandLocationsMap[b.id] || [];
                    return (
                      <Flex key={b.id} direction="column" gap="2" style={{ width: "100%" }}>
                        <Flex
                          direction="row"
                          style={{ width: "100%", alignItems: "center", gap: 8 }}
                        >
                          <Text
                            size="2"
                            weight="medium"
                            style={{
                              flex: "1 1 0%",
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            [{b.name}]
                          </Text>

                          {(brandLocations || []).length > 0 && (
                            <Flex
                              style={{
                                marginLeft: "8px",
                                gap: 8,
                                alignItems: "center",
                                flexShrink: 0,
                              }}
                            >
                              <Select.Root
                                value={brandListSelections[String(b.id)] || ""}
                                onValueChange={(v) => handleBrandListSelectionChange(b.id, v)}
                                style={{ width: 200, minWidth: 120, flexShrink: 0 }}
                              >
                                <Select.Trigger
                                  placeholder="Apply venue to brand..."
                                  style={{ width: "200px", boxSizing: "border-box" }}
                                >
                                  <div
                                    style={{
                                      textOverflow: "ellipsis",
                                      overflow: "hidden",
                                      whiteSpace: "nowrap",
                                      width: "100%",
                                    }}
                                  >
                                    {brandListSelections[String(b.id)]
                                      ? sevenroomsVenues.find(
                                          (bl) =>
                                            String(bl.id) ===
                                            String(brandListSelections[String(b.id)])
                                        )?.name || ""
                                      : "Apply venue to brand..."}
                                  </div>
                                </Select.Trigger>
                                <Select.Content side="bottom" align="start" sideOffset={4}>
                                  {sevenroomsVenues.map((bl) => (
                                    <Select.Item key={bl.id} value={bl.id} title={bl.name}>
                                      {bl.name}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                              <Button
                                size="1"
                                variant="soft"
                                onClick={() => handleApplyBrandList(b.id)}
                                style={{ flexShrink: 0 }}
                              >
                                Apply
                              </Button>
                            </Flex>
                          )}
                        </Flex>

                        {(brandLocations || []).length > 0 ? (
                          brandLocations.map(renderLocationRow)
                        ) : (
                          <Text size="2" style={{ color: "var(--gray-a9)" }}>
                            No locations for this brand
                          </Text>
                        )}
                      </Flex>
                    );
                  })
                ) : (
                  <Text size="2" color="gray">
                    No brands found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Locations
                  </Text>
                  <Text size="1" color="gray">
                    Map independent b connect locations to a SevenRooms venue.
                  </Text>
                </Flex>

                <SevenRoomsLocationPairHeaders />

                {(independentLocations || []).length > 0 ? (
                  independentLocations.map(renderLocationRow)
                ) : (
                  <Text size="2" color="gray">
                    No locations found.
                  </Text>
                )}
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Client tag
                  </Text>
                  <Text size="1" color="gray">
                    Identify contacts sent to SevenRooms by this integration.
                  </Text>
                </Flex>
                <SevenRoomsClientTagSection
                  tagGroup={clientTagGroup}
                  tagName={clientTagName}
                  onTagGroupChange={setClientTagGroup}
                  onTagNameChange={setClientTagName}
                />
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Data fields
                  </Text>
                  <Text size="1" color="gray">
                    Map captive portal fields to SevenRooms client columns. Fields a guest did
                    not enter are skipped automatically.
                  </Text>
                </Flex>
                <SevenRoomsRequiredFieldsNotice />
                <SevenRoomsDataFieldsSection fieldMappings={fieldMappings} />
              </Flex>

              <Separator orientation="horizontal" size="4" />

              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">
                    Sync directions
                  </Text>
                  <Text size="1" color="gray">
                    Choose push only, pull only, or both. At least one direction is required.
                  </Text>
                </Flex>
                <SevenRoomsSyncDirectionsSection
                  pushChecked={pushEnabled}
                  pullChecked={pullEnabled}
                  onPushCheckedChange={handlePushCheckedChange}
                  onPullCheckedChange={handlePullCheckedChange}
                />
              </Flex>
            </Flex>
          </Box>

          <Flex justify="end" gap="3" style={{ flex: "0 0 auto" }}>
            <Dialog.Close>
              <Button size="2" variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              size="2"
              variant="soft"
              color="accent"
              loading={isLoading}
              disabled={isLoading || (!pushEnabled && !pullEnabled)}
              onClick={handleSaveLocationMappings}
            >
              Save and finish
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>

      <div style={{ position: "fixed", zIndex: 2147483647, pointerEvents: "none" }}>
        <ToastAlert />
      </div>
    </Dialog.Root>

    <SevenRoomsPushScopeDialog
      isOpen={pushScopeDialogOpen}
      onOpenChange={setPushScopeDialogOpen}
      onChoose={handlePushScopeChosen}
    />

    <SevenRoomsPullScopeDialog
      isOpen={pullScopeDialogOpen}
      onOpenChange={setPullScopeDialogOpen}
      onChoose={handlePullScopeChosen}
    />
    </>
  );
}
